import type {LayoutPayload} from '@github-ui/code-view-types'
import {CurrentRepositoryProvider, type Repository} from '@github-ui/current-repository'
import {CurrentUserProvider} from '@github-ui/current-user'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize, ScreenSizeProvider} from '@github-ui/screen-size'
import {CodeViewOptionsProvider} from '@github-ui/use-code-view-options'
import React, {useEffect} from 'react'

export function App({children}: {children?: React.ReactNode}) {
  const payload = useRoutePayload<LayoutPayload>()
  const [repo] = React.useState(payload?.repo)

  return (
    <AppWrapper payload={payload} repo={repo}>
      {children}
    </AppWrapper>
  )
}

// `repo` is an explicit prop so that each caller can control its referential stability:
// - `App` (jsonRoute path) freezes it via useState to avoid re-renders from useRoutePayload.
// - `CodeViewLayout` (ui-service path) passes data.repo directly so it updates on cross-repo
//   soft navigations (e.g. via the RepoPicker). Freezing isn't needed here because
//   useRouteQuery is backed by React Query, which preserves object identity via
//   structuralSharing when the data hasn't changed.
export function AppWrapper({
  children,
  payload,
  repo,
}: {
  children?: React.ReactNode
  payload: LayoutPayload
  repo: Repository
}) {
  const [user] = React.useState(payload?.currentUser)

  useEffect(() => {
    const footer = document.querySelector('.footer')
    if (footer) {
      footer.querySelector('.mt-6')?.classList.replace('tmp-mt-6', 'mt-0')
      footer.querySelector('.border-top')?.classList.remove('border-top')
    }
  }, [])

  return (
    <>
      {/*
       *React hoists this meta tag to the <head> ensuring that we have one, even if turbo wouldn't add one.
       * We should still set this on the server though, to ensure that stats can be collected before hydration
       * as well.
       */}
      <meta name="github-code-view-meta-stats" id="github-code-view-meta-stats" data-hydrostats="publish" />
      <ScreenSizeProvider initialValue={ScreenSize.xxxlarge}>
        <CurrentUserProvider user={user}>
          <CurrentRepositoryProvider repository={repo}>
            <CodeViewOptionsProvider>{children}</CodeViewOptionsProvider>
          </CurrentRepositoryProvider>
        </CurrentUserProvider>
      </ScreenSizeProvider>
    </>
  )
}
